import { getLastMessagePreview } from "@/views/my-patients/room-list";
import moment from "moment";

export default {
  namespaced: true,

  state: {
    roomList: [],
    currentRoomId: null,       // accumulated patient items from 
    topRoomId: '', //the room which should be put on the top
    currentChangeUnreadRoom: {
      roomId: '',
      unreadCount: 0
    }, //the room which just changed the unread count
    lastSeenMap: {}, // Map of room last-seen data: { roomId: { ls, unread, userMentions, groupMentions, ... } }
  },

  getters: {
    getPatientByRoomId: (state) => (roomId) => {
      return state.roomList.find(patient => patient.roomId === roomId);
    },
    getLastSeenByRoomId: (state) => (roomId) => {
      return state.lastSeenMap[roomId] || null;
    },
  },

  mutations: {
    SET_ROOM_LIST(state, { roomList }) {
      state.roomList = roomList;
    },
    ADD_ROOM(state, { room }) {
      state.roomList.unshift(room);
    },
    SET_CURRENT_ROOM_ID(state, { roomId }) {
      state.currentRoomId = roomId;
    },
    SET_UNREAD_COUNT(state, { roomId, unreadCount }) {
      let room = state.roomList.find(r => r.roomId === roomId);
      if (room) {
        room.unreadCount = unreadCount || 0;
      }
    },
    // update last message of a room
    UPDATE_ROOM_LAST_MESSAGE(state, { roomId, message }) {
      const room = state.roomList.find(room => room.roomId === roomId);
      if (room) {
        room.lastMessage = getLastMessagePreview(message);
        room.lastMessageTime = moment().utc().format();
      }
    },
    // update top room id
    UPDATE_TOP_ROOM_ID(state, roomId) {
      state.topRoomId = roomId;
    },
    // update current change unread room
    UPDATE_CURRENT_CHANGE_UNREAD_ROOM(state, { roomId, unreadCount }) {
      state.currentChangeUnreadRoom = { roomId, unreadCount };
    },
    UPDATE_ROOM_ID(state, { patientId, roomId }) {
      const room = state.roomList.find(
        r => r.patientBasicInfo?.patientId?.toString() === patientId?.toString()
      );
      if (room) {
        room.roomId = roomId;
      }
    },
    UPDATE_PATIENT_CONTACT_INFO(state, { roomId, contactInfo }) {
      const room = state.roomList.find(r => r.roomId === roomId);
      if (room && room.patientBasicInfo) {
        // Update contact information in patientBasicInfo
        if (contactInfo.phone !== undefined) {
          room.patientBasicInfo.phone = contactInfo.phone;
        }
        if (contactInfo.email !== undefined) {
          room.patientBasicInfo.email = contactInfo.email;
        }
        if (contactInfo.street_address !== undefined || contactInfo.city !== undefined || 
            contactInfo.state !== undefined || contactInfo.zipcode !== undefined || 
            contactInfo.country !== undefined) {
          // Update addressObj with structured data
          if (!room.patientBasicInfo.addressObj) {
            room.patientBasicInfo.addressObj = {};
          }
          if (contactInfo.street_address !== undefined) {
            room.patientBasicInfo.addressObj.streetAddress = contactInfo.street_address;
          }
          if (contactInfo.city !== undefined) {
            room.patientBasicInfo.addressObj.city = contactInfo.city;
          }
          if (contactInfo.state !== undefined) {
            room.patientBasicInfo.addressObj.state = contactInfo.state;
          }
          if (contactInfo.zipcode !== undefined) {
            room.patientBasicInfo.addressObj.zipCode = contactInfo.zipcode;
          }
          if (contactInfo.country !== undefined) {
            room.patientBasicInfo.addressObj.country = contactInfo.country;
          }
          
          // Also reconstruct full address string for display
          const parts = [];
          if (contactInfo.street_address) parts.push(contactInfo.street_address);
          if (contactInfo.city) parts.push(contactInfo.city);
          if (contactInfo.state && contactInfo.zipcode) {
            parts.push(`${contactInfo.state} ${contactInfo.zipcode}`);
          } else if (contactInfo.state) {
            parts.push(contactInfo.state);
          } else if (contactInfo.zipcode) {
            parts.push(contactInfo.zipcode);
          }
          room.patientBasicInfo.address = parts.join(', ');
        }
      }
    },
    UPDATE_PATIENT_DEMOGRAPHICS(state, { roomId, demographics }) {
      const room = state.roomList.find(r => r.roomId === roomId);
      if (room && room.patientBasicInfo) {
        // Update demographics information in patientBasicInfo
        if (demographics.dob !== undefined) {
          room.patientBasicInfo.dob = demographics.dob;
        }
        if (demographics.gender !== undefined) {
          room.patientBasicInfo.gender = demographics.gender;
        }
      }
    },
    // Update last-seen data for multiple rooms
    UPDATE_LAST_SEEN_MAP(state, { updates }) {
      updates.forEach(update => {
        state.lastSeenMap = {
          ...state.lastSeenMap,
          [update.rid]: {
            _id: update._id,
            rid: update.rid,
            name: update.name,
            t: update.t,
            ls: update.ls,
            ts: update.ts,
            unread: update.unread,
            userMentions: update.userMentions,
            groupMentions: update.groupMentions
          }
        };
      });
    },
    // Update last-seen data for a single room
    UPDATE_ROOM_LAST_SEEN(state, { roomId, lastSeenData }) {
      state.lastSeenMap = {
        ...state.lastSeenMap,
        [roomId]: lastSeenData
      };
    },
  },

  actions: {
    setRoomList({ commit, state }, roomList) {
      commit('SET_ROOM_LIST', { roomList });
    },
    addRoom({ commit }, { room }) {
      commit('ADD_ROOM', { room });
    },
    setCurrentRoomId({ commit }, roomId) {
      commit('SET_CURRENT_ROOM_ID', { roomId });
    },
    setUnreadCount({ commit }, { roomId, unreadCount }) {
      commit('SET_UNREAD_COUNT', { roomId, unreadCount });
    },
    updateRoomLastMessage({ commit }, { roomId, message }) {
      commit('UPDATE_ROOM_LAST_MESSAGE', { roomId, message });
    },
    updateTopRoomId({ commit }, roomId) {
      commit('UPDATE_TOP_ROOM_ID', roomId);
    },
    updateCurrentChangeUnreadRoom({ commit }, { roomId, unreadCount }) {
      commit('UPDATE_CURRENT_CHANGE_UNREAD_ROOM', { roomId, unreadCount });
    },
    updateRoomId({ commit }, { patientId, roomId }) {
      commit('UPDATE_ROOM_ID', { patientId, roomId });
    },
    updatePatientContactInfo({ commit }, { roomId, contactInfo }) {
      commit('UPDATE_PATIENT_CONTACT_INFO', { roomId, contactInfo });
    },
    updatePatientDemographics({ commit }, { roomId, demographics }) {
      commit('UPDATE_PATIENT_DEMOGRAPHICS', { roomId, demographics });
    },
    updateLastSeenMap({ commit }, { updates }) {
      commit('UPDATE_LAST_SEEN_MAP', { updates });
    },
    updateRoomLastSeen({ commit }, { roomId, lastSeenData }) {
      commit('UPDATE_ROOM_LAST_SEEN', { roomId, lastSeenData });
    },
  },
};
